import React, {useCallback, useEffect, useMemo} from "react";
import { Row, Col, Card, CardHeader, CardBody } from 'reactstrap';
import "react-toastify/dist/ReactToastify.css";
import Button from "react-bootstrap/Button";
import RightModal from "./modalForm";

// hooks
import { useRedux, useHistory, useFareRestriction, useSearchInput } from "../../../hooks";

import {
    editDataSaleCurrencyRate, getSaleCurrencies,
    getSaleCurrencyRates,
    onRemoveSaleCurrencyRate,
    removeSaleCurrencyRateModalHide,
    removeSaleCurrencyRateModalShow,
    showRightModalSaleCurrencyRate
} from "../../../redux/actions";

import TableList from "../../../components/TableList";
import SaleCurrencyRateColumns from "./tableColumns";
import {CurrencyRateType} from "../../../redux/Sale/CurrencyRate/types";
import {EntityActionsHandlersType} from "../../../redux/types";
import RemoveModal from "../../../components/RemoveForm";

import '../../../i18/config';
import {useTranslation} from "react-i18next";
import BreadCrumb from "../../../components/Common/BreadCrumb";
import Loader from "../../../components/Loader";
import MainListFilter from "../../../components/MainListFilter";

const SaleCurrencyRateIndex = () => {
    const { dispatch, useAppSelector } = useRedux();

    const { t } = useTranslation(['common']);

    const componentType = 'saleCurrencyRateTable';

    const {
        historyHash,
        searchPhrase,
        loadItems,
        handleSearchClick
    } = useHistory({
        componentType,
        loadAction: getSaleCurrencyRates,
        dispatch
    });

    const { onSearchInput } = useSearchInput(handleSearchClick);

    const {
        saleCurrency,
        saleCurrencyRate,
    } = useAppSelector(state => ({
        saleCurrency: state.SaleCurrency,
        saleCurrencyRate: state.SaleCurrencyRate
    }));

    useEffect(() => {
        dispatch(getSaleCurrencies({maxResults:1000}));
    }, [dispatch]);

    const isFetched = useMemo(() => (
        saleCurrency.isFetched &&
        saleCurrencyRate.isFetched
    ),[
        saleCurrency.isFetched,
        saleCurrencyRate.isFetched
    ]);

    const openRightModal = useCallback((event:any) => {
        if ((event !== undefined) && (event.uuid !== undefined) && (event.uuid.length>0)){
            dispatch(editDataSaleCurrencyRate({data:event, modalEditTitle: 'changeCurrencyRate'}));
        } else {
            dispatch(showRightModalSaleCurrencyRate({data:[], modalEditTitle: 'newCurrencyRate'}));
        }
    },[dispatch]);

    const onEntityRemoveModalShow = useCallback((event:any) => {
        if ((event !== undefined) && (event.uuid !== undefined) && (event.uuid.length>0)){
            dispatch(removeSaleCurrencyRateModalShow({data:event, modalEditTitle: 'deleteCurrencyRate'}));
        }
    },[dispatch]);

    const onEntityRemoveModalHide = useCallback(() => dispatch(removeSaleCurrencyRateModalHide()),[dispatch]);

    const onEntityRemove = useCallback((event:any) => {
        if ((event !== undefined) && (event.uuid !== undefined) && (event.uuid.length>0)){
            dispatch(onRemoveSaleCurrencyRate({data:event, modalEditTitle: 'deleteCurrencyRate'}));
        }
    },[dispatch]);

    useEffect(() => {
        if (saleCurrencyRate.isNeedReload){
            loadItems();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadItems, saleCurrencyRate.isNeedReload]);

    const EntityActionsHandlers:EntityActionsHandlersType = {
        onEdit: openRightModal,
        onRemove: onEntityRemoveModalShow
    }

    const onTableRowClick = useCallback((item:CurrencyRateType) => {
        if (item.actions && item.actions.canEdit){
            openRightModal(item);
        }
    },[openRightModal]);

    const columns = SaleCurrencyRateColumns(EntityActionsHandlers);

    return (
        <>
            <BreadCrumb title={t('menu:currencyRates')} pageTitle={t('menu:main')} />
                <Row className="g-4 mb-3">
                    <div className="col-sm-auto">
                        <div>
                            <Button variant="secondary" onClick={openRightModal} className="btn btn-success">
                                <i className="ri-add-line align-bottom me-1"/> {t('common:addNew')}
                            </Button>
                        </div>
                    </div>
                </Row>

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="border-bottom-0">
                                <h5 className="card-title mb-0">{t('currency:currencyRates')}</h5>
                            </CardHeader>
                            <CardBody className="border border-dashed border-end-0 border-start-0 pb-3">
                                <MainListFilter
                                    onClick={handleSearchClick}
                                    value={searchPhrase}
                                    onChange={onSearchInput}
                                />
                            </CardBody>
                            <CardBody>
                                {isFetched ?
                                    <TableList
                                    columns={columns}
                                    data={saleCurrencyRate.items}
                                    onRowClick={onTableRowClick}
                                    key={componentType}
                                    historyHash = {historyHash}
                                    paginationTotalRows={saleCurrencyRate?.meta?.totalCount || 0}
                                    paginationServer={true}
                                    isShowChangePageSize={true}
                                    />
                                    :
                                    <Loader/>
                                }
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <RightModal/>
                <RemoveModal
                    onRemove={onEntityRemove}
                    handleClose={onEntityRemoveModalHide}
                    removeItem={saleCurrencyRate.selectedItem}
                    isOpen={saleCurrencyRate.removeModalShow}
                    modalTitle={saleCurrencyRate.modalEditTitle}/>
        </>
    );
};

export default SaleCurrencyRateIndex;


